import {
  type ApolloCache,
  type ApolloError,
  useApolloClient,
} from '@apollo/client';
import type { Language } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import { useAppConfig } from 'src/modules/appConfig';
import type { Maybe } from 'src/types';
import * as Schema from 'src/types/graphql';

import { useCandidateId } from '../../data';
import {
  selectAvailability,
  selectAvailabilityFailure,
  selectAvailabilitySuccess,
} from '../actionCreators';

import translations from './.vocab';
import {
  generateUpdateAvailabilityOptimisticResponse,
  updateCacheUpdateAvailability,
} from './apolloCache';

export const useAvailability = () => {
  const { language } = useAppConfig();

  const { data, loading, error } = useQuery(Schema.GetAvailabilityDocument, {
    variables: {
      languageCode: language,
    },
  });

  const isLoadingOrError = loading || error;

  const availability =
    isLoadingOrError || typeof data?.viewer?.availability === 'undefined'
      ? null
      : data?.viewer?.availability;

  return { availability, loading, error };
};

const key = 'next-role-availability';

export const useUpdateAvailability = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();
  const apolloClient = useApolloClient();

  const onMutate = () => {
    dispatch(selectAvailability());
  };
  const onCompleted = () => {
    dispatch(selectAvailabilitySuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(selectAvailabilityFailure(error));
  };

  return useToastMutation(Schema.UpdateAvailabilityDocument, {
    key,
    errorActionRouteParameters: {
      card: 'availability',
      mode: 'EDIT',
    },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Availability updated'),
    update: updateCacheUpdateAvailability,
    optimisticResponse: generateUpdateAvailabilityOptimisticResponse({
      _id: candidateId || '',
      apolloClient,
    }),
  });
};

export const useAvailabilityOptions = () => {
  const { language } = useAppConfig();

  const { data, loading, error, refetch } = useQuery(
    Schema.GetAvailabilityOptionsDocument,
    {
      variables: {
        languageCode: language,
      },
      bypassErrorPageOnFail: true,
    },
  );

  const availabilityOptionsData =
    loading || error ? [] : data?.availabilityOptions || [];

  return {
    availabilityOptions: availabilityOptionsData,
    loading,
    error,
    refetch,
  };
};

export const getAvailabilityOptionsFromCache = <T extends Record<string, any>>(
  cache: ApolloCache<T>,
  languageCode: Language,
) => {
  const data = getDataFromCache({
    cache,
    query: Schema.GetAvailabilityOptionsDocument,
    variables: { languageCode },
  });
  const availabilityOptions = data?.availabilityOptions || [];

  return availabilityOptions;
};

/** Returns a mock response for use with the apollo MockedProvider */
export const mockGetAvailabilityRequest = (
  availability: Maybe<Schema.Availability> = null,
) => ({
  request: {
    query: Schema.GetAvailabilityDocument,
    variables: { languageCode: 'en' },
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        availability,
      },
    },
  },
});
